import { useCallback, useEffect, useMemo, useState } from 'react';
import { CheckCircle2, KeyRound, Plus, RefreshCw, Sparkles } from 'lucide-react';
import { formatDistanceToNowStrict } from 'date-fns';
import { useApi } from '../contexts/ApiContext';
import type { ApiResponse } from '../types';
import PageHeader from '../components/ui/PageHeader';
import { SkeletonCard } from '../components/ui/Skeleton';
import { useToast } from '../components/ui/Toast';

interface Row {
  id: string;
  registrar_type: string;
  registrar_name: string;
  login: string;
  owner: string | null;
  purpose: string;
  display_name: string | null;
  domain_count: number;
  api_ready: number | null;
  credential_source: string;
  vault: string | null;
  onepassword_title_synced_at: string | null;
  suggestion: { owner: string | null; purpose: string | null } | null;
}
interface Data { accounts: Row[]; owners: { id: string; name: string; accounts: number }[]; purposes: string[] }
interface OnePasswordResult { matching: number; needs_rename: { account: string; current: string; expected: string }[]; failed: { account: string; error: string }[] }

const PURPOSE_LABEL: Record<string, string> = { portfolio: 'Portfolio', bidding: 'Bidding', client: 'Client', legacy: 'Legacy' };

/**
 * Admin page: give every registrar account an owner entity and a purpose, which
 * generate its standard name ("GoDaddy · Clockwork Media"). The login stays as
 * the identifier. Changes rename the account's 1Password item to match.
 */
export default function AccountNamesPage() {
  const { fetchApi } = useApi();
  const toast = useToast();
  const [data, setData] = useState<Data | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [onlyUnnamed, setOnlyUnnamed] = useState(false);
  const [newOwner, setNewOwner] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await fetchApi<ApiResponse<Data>>('/accounts/naming');
      if (r.data) setData(r.data);
    } catch (e) {
      toast.error('Could not load accounts', e instanceof Error ? e.message : undefined);
    }
  }, [fetchApi, toast]);
  useEffect(() => { load(); }, [load]);

  // Renames are done with the 1Password CLI; DomainHQ only checks titles.
  const report1p = (op: OnePasswordResult | undefined) => {
    if (!op) return;
    if (op.failed.length) toast.error(`1Password: couldn't read ${op.failed.length} item${op.failed.length === 1 ? '' : 's'}`, op.failed[0].error);
    else if (op.needs_rename.length) toast.success(`${op.needs_rename.length} 1Password item${op.needs_rename.length === 1 ? '' : 's'} to rename`, `e.g. “${op.needs_rename[0].current}” → “${op.needs_rename[0].expected}” (rename with the 1Password CLI)`);
  };

  const save = async (row: Row, patch: { owner?: string | null; purpose?: string }) => {
    setBusy(row.id);
    try {
      const r = await fetchApi<ApiResponse<{ onepassword: OnePasswordResult }>>(`/accounts/${encodeURIComponent(row.id)}/naming`, { method: 'PUT', body: JSON.stringify(patch) });
      report1p(r.data?.onepassword);
      await load();
    } catch (e) {
      toast.error('Could not save', e instanceof Error ? e.message : undefined);
    } finally { setBusy(null); }
  };

  const applySuggestions = async () => {
    if (!data) return;
    setBusy('all');
    let n = 0;
    try {
      for (const row of data.accounts.filter((r) => r.suggestion && !r.owner && r.suggestion.owner)) {
        await fetchApi(`/accounts/${encodeURIComponent(row.id)}/naming`, { method: 'PUT', body: JSON.stringify({ owner: row.suggestion!.owner, ...(row.suggestion!.purpose ? { purpose: row.suggestion!.purpose } : {}) }) });
        n++;
      }
      toast.success(`Applied ${n} suggestion${n === 1 ? '' : 's'}`, 'Their 1Password items need renaming to match (1Password CLI).');
      await load();
    } catch (e) {
      toast.error('Stopped applying suggestions', e instanceof Error ? e.message : undefined);
      await load();
    } finally { setBusy(null); }
  };

  const addOwner = async () => {
    if (!newOwner.trim()) return;
    try {
      await fetchApi('/accounts/owners', { method: 'POST', body: JSON.stringify({ name: newOwner }) });
      setNewOwner('');
      await load();
    } catch (e) {
      toast.error('Could not add owner', e instanceof Error ? e.message : undefined);
    }
  };

  const checkAll = async () => {
    setBusy('sync');
    try {
      const r = await fetchApi<ApiResponse<OnePasswordResult>>('/accounts/naming/check-1password', { method: 'POST' });
      if (r.data?.failed.length || r.data?.needs_rename.length) report1p(r.data);
      else toast.success('1Password titles match', `${r.data?.matching ?? 0} items checked`);
      await load();
    } catch (e) {
      toast.error('1Password check failed', e instanceof Error ? e.message : undefined);
    } finally { setBusy(null); }
  };

  const rows = useMemo(() => (data?.accounts ?? []).filter((r) => !onlyUnnamed || !r.owner), [data, onlyUnnamed]);
  if (!data) return <div className="space-y-6"><PageHeader title="Account names" /><SkeletonCard lines={8} /></div>;
  const unnamed = data.accounts.filter((r) => !r.owner).length;
  const suggestable = data.accounts.filter((r) => !r.owner && r.suggestion?.owner).length;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Account names"
        description="Every registrar account is named from its owner (the legal entity) and purpose — e.g. “GoDaddy · Clockwork Media” or “Dynadot · Paradise Media · Bidding”. The login stays as the identifier. Each 1Password item should be titled to match — “GoDaddy · Clockwork Media (login)” — renamed with the 1Password CLI; “Check 1Password titles” shows which still differ."
        meta={<span>{data.accounts.length - unnamed} of {data.accounts.length} named</span>}
        actions={
          <div className="flex flex-wrap gap-2">
            {suggestable > 0 && (
              <button className="btn btn-secondary" onClick={applySuggestions} disabled={busy !== null}>
                <Sparkles className="mr-2 h-4 w-4" />Apply {suggestable} suggestion{suggestable === 1 ? '' : 's'}
              </button>
            )}
            <button className="btn btn-secondary" onClick={checkAll} disabled={busy !== null} title="Compare 1Password item titles with these names (read-only)">
              <RefreshCw className={`mr-2 h-4 w-4 ${busy === 'sync' ? 'animate-spin' : ''}`} />Check 1Password titles
            </button>
          </div>
        }
      />

      <div className="card p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="label" htmlFor="new-owner">Add an owner entity</label>
            <input id="new-owner" className="input w-64" value={newOwner} onChange={(e) => setNewOwner(e.target.value)} placeholder="e.g. Clockwork Media Ltd"
              onKeyDown={(e) => { if (e.key === 'Enter') addOwner(); }} />
          </div>
          <button className="btn btn-secondary" onClick={addOwner} disabled={!newOwner.trim()}><Plus className="mr-1 h-4 w-4" />Add</button>
          <div className="flex flex-wrap gap-1.5 pb-1.5">
            {data.owners.map((o) => <span key={o.id} className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-700">{o.name} <span className="text-slate-400">{o.accounts}</span></span>)}
          </div>
          <label className="ml-auto flex items-center gap-2 pb-1.5 text-sm text-slate-600">
            <input type="checkbox" checked={onlyUnnamed} onChange={(e) => setOnlyUnnamed(e.target.checked)} />Only unnamed ({unnamed})
          </label>
        </div>
      </div>

      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
              <th className="px-4 py-2.5">Registrar</th>
              <th className="px-4 py-2.5">Login</th>
              <th className="px-4 py-2.5 text-right">Domains</th>
              <th className="px-4 py-2.5">Owner</th>
              <th className="px-4 py-2.5">Purpose</th>
              <th className="px-4 py-2.5">Name</th>
              <th className="px-4 py-2.5">1Password</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((r) => (
              <tr key={r.id} className={busy === r.id ? 'opacity-60' : ''}>
                <td className="whitespace-nowrap px-4 py-2 text-slate-700">{r.registrar_name}</td>
                <td className="px-4 py-2"><code className="break-all text-xs text-slate-600">{r.login}</code>{r.vault && <div className="text-[11px] text-slate-400">{r.vault}</div>}</td>
                <td className="px-4 py-2 text-right tabular-nums text-slate-600">{r.domain_count}</td>
                <td className="px-4 py-2">
                  <select className="input py-1 text-sm" value={r.owner ?? ''} disabled={busy !== null} onChange={(e) => save(r, { owner: e.target.value || null })}>
                    <option value="">— Unassigned —</option>
                    {data.owners.map((o) => <option key={o.id} value={o.name}>{o.name}</option>)}
                    {r.suggestion?.owner && !data.owners.some((o) => o.name === r.suggestion!.owner) && <option value={r.suggestion.owner}>{r.suggestion.owner} (new)</option>}
                  </select>
                  {!r.owner && r.suggestion && (
                    <button type="button" className="mt-1 block text-[11px] text-brand-600 hover:underline" disabled={busy !== null}
                      onClick={() => save(r, { ...(r.suggestion!.owner ? { owner: r.suggestion!.owner } : {}), ...(r.suggestion!.purpose ? { purpose: r.suggestion!.purpose } : {}) })}>
                      Suggested: {[r.suggestion.owner, r.suggestion.purpose ? PURPOSE_LABEL[r.suggestion.purpose] : null].filter(Boolean).join(' · ')}
                    </button>
                  )}
                </td>
                <td className="px-4 py-2">
                  <select className="input py-1 text-sm" value={r.purpose} disabled={busy !== null} onChange={(e) => save(r, { purpose: e.target.value })}>
                    {data.purposes.map((p) => <option key={p} value={p}>{PURPOSE_LABEL[p] ?? p}</option>)}
                  </select>
                </td>
                <td className="px-4 py-2 font-medium text-slate-900">{r.display_name ? `${r.registrar_name} · ${r.display_name}` : <span className="font-normal text-slate-400">Needs an owner</span>}</td>
                <td className="whitespace-nowrap px-4 py-2 text-xs text-slate-500">
                  {r.credential_source !== '1password' ? <span className="text-slate-400">Not in 1Password</span>
                    : r.onepassword_title_synced_at ? <span className="inline-flex items-center gap-1 text-green-700"><CheckCircle2 className="h-3.5 w-3.5" />Title matches · checked {formatDistanceToNowStrict(new Date(r.onepassword_title_synced_at), { addSuffix: true })}</span>
                    : r.display_name ? <span className="inline-flex items-center gap-1 text-amber-700"><KeyRound className="h-3.5 w-3.5" />Needs renaming</span>
                    : <span className="text-slate-400">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
